import { PropertyDefinition } from 'types/contacts'

import {
  AVAILABLE_OPERATORS,
  PROMPT_MODES,
  QUALIFICATION_DEFAULT_NAME,
} from '../constants'
import { Attribute, PersonaConfig, PersonaResponse } from '../types'
import { PersonaForm, PersonaFormAttributes } from './types'

export const transformAttributes = (
  data: Attribute[]
): PersonaForm['attributes'] => {
  return data.map(({ name, operator, values, type }) => ({
    name: { label: name, value: name, type },
    operator: operator ? { label: operator, value: operator } : undefined,
    value:
      operator === AVAILABLE_OPERATORS.INCLUDES ||
      operator === AVAILABLE_OPERATORS.IS_BETWEEN
        ? values
        : (values && Array.isArray(values) && values[0]) || '',
  }))
}

export const buildAttributesPayload = (
  data: PersonaForm['attributes']
): PersonaResponse['attributes'] => {
  let formattedAttributes = data.map(({ name, operator, value }): Attribute => {
    let values: string[] = []
    if (value && Array.isArray(value)) values = value
    else if (value) values = [value]

    return {
      name: name?.value || '',
      operator: operator?.value,
      values,
      type: name?.type || '',
    }
  })

  formattedAttributes = formattedAttributes.filter(
    (c, index, allAttributes) =>
      index ===
      allAttributes.findIndex((t) => JSON.stringify(t) === JSON.stringify(c))
  )

  return formattedAttributes
}

export const transformInitialValues = ({
  data,
  contactAttributes,
}: {
  data?: PersonaConfig
  contactAttributes: PropertyDefinition[]
}): PersonaForm => {
  const mode = data?.mode || PROMPT_MODES.AI
  const attributes =
    data?.attributes.map(({ name, operator, values, type }) => {
      const existingContactAttribute = contactAttributes.find(
        ({ name: contactAttributeName }) => contactAttributeName === name
      )
      return {
        name: existingContactAttribute
          ? {
              label: existingContactAttribute.displayName,
              value: existingContactAttribute.name,
              type: existingContactAttribute.type,
            }
          : { label: name, value: name, type },
        operator: operator ? { label: operator, value: operator } : undefined,
        value:
          operator === AVAILABLE_OPERATORS.INCLUDES ||
          operator === AVAILABLE_OPERATORS.IS_BETWEEN
            ? values
            : (values && Array.isArray(values) && values[0]) || '',
      }
    }) || []

  return {
    name: data?.name || QUALIFICATION_DEFAULT_NAME,
    prompt: data?.prompt || '',
    mode,
    hasAcknowledge: false,
    attributes,
    foundAIAttributes: data?.prompt ? attributes : undefined,
  }
}

const getAttributesObj = (arr: PersonaFormAttributes[]) => {
  const initialObject: Record<string, PersonaFormAttributes> = {}

  return arr.reduce((prev, current) => {
    const name = current.name?.value
    const operator = current.operator?.value

    if (name && operator) {
      return {
        ...prev,
        [`${name}${operator}`]: current,
      }
    }

    return prev
  }, initialObject)
}

const isDeepEqual = (
  obj1: PersonaFormAttributes,
  obj2: PersonaFormAttributes
): boolean => JSON.stringify(obj1) === JSON.stringify(obj2)

export const getDifference = (
  defaultValues: PersonaFormAttributes[],
  newValues: PersonaFormAttributes[]
) => {
  const result = {
    changed: [] as PersonaFormAttributes[],
    deleted: [] as PersonaFormAttributes[],
  }

  const defaultObj = getAttributesObj(defaultValues)
  const newObj = getAttributesObj(newValues)

  Object.keys(newObj).forEach((newKey) => {
    if (
      !defaultObj.hasOwnProperty(newKey) ||
      (defaultObj[newKey] && !isDeepEqual(defaultObj[newKey], newObj[newKey]))
    )
      result.changed.push(newObj[newKey])
  })

  Object.keys(defaultObj).forEach((defaultKey) => {
    if (!newObj.hasOwnProperty(defaultKey))
      result.deleted.push(defaultObj[defaultKey])
  })

  return result
}
